import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, Share2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const BlogPostPage = () => {
  const { blogId } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlog();
  }, [blogId]);

  const fetchBlog = async () => {
    try {
      const response = await axios.get(`${API}/api/blog/${blogId}`);
      setBlog(response.data);
    } catch (error) {
      console.error('Failed to fetch blog:', error);
      toast.error('Blog post not found');
      navigate('/blog');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: blog.title,
          text: blog.excerpt,
          url: url
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-cyan-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!blog) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header Image */}
      {blog.cover_image_url && (
        <div className="h-64 sm:h-96 w-full overflow-hidden">
          <img
            src={blog.cover_image_url}
            alt={blog.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Back button */}
        <Link
          to="/blog"
          className="inline-flex items-center text-cyan-600 hover:text-cyan-700 mb-6 font-semibold"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Blog
        </Link>

        {/* Title and Meta */}
        <header className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 mb-4">
            {blog.title}
          </h1>
          
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-2 text-slate-500">
              <Calendar className="w-5 h-5" />
              <span>{formatDate(blog.created_at)}</span>
            </div>
            
            <button
              onClick={handleShare}
              className="flex items-center gap-2 text-cyan-600 hover:text-cyan-700 font-semibold"
            >
              <Share2 className="w-5 h-5" />
              Share
            </button>
          </div>
        </header>

        {/* Blog Content - Rendered as HTML */}
        <article 
          className="prose prose-lg prose-slate max-w-none
            prose-headings:font-bold prose-headings:text-slate-900
            prose-a:text-cyan-600 prose-a:no-underline hover:prose-a:underline
            prose-img:rounded-xl prose-img:shadow-lg
            prose-video:rounded-xl prose-video:shadow-lg"
          dangerouslySetInnerHTML={{ __html: blog.content }}
        />

        {/* Footer */}
        <footer className="mt-12 pt-8 border-t border-slate-200">
          <div className="flex justify-between items-center">
            <Link
              to="/blog"
              className="text-cyan-600 hover:text-cyan-700 font-semibold"
            >
              ← More posts
            </Link>
            
            <button
              onClick={handleShare}
              className="bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-2 rounded-full font-bold"
            >
              Share this post
            </button>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default BlogPostPage;
